'use client';

/**
 * RFQActions - RFQ-level quick actions
 *
 * Available actions:
 * - Send to vendors (if draft)
 * - Compare quotes (if responses received)
 * - Add products to RFQ
 * - View in Odoo
 * - Download as PDF
 *
 * @component_role RFQ-level Actions
 * @business_rule RFQ lifecycle actions: Send to Vendors (WhatsApp), Add Products
 *   (browse catalogs), Compare Quotes (open comparison matrix), Award Vendor
 *   (convert to PO). Status-aware - shows relevant actions per RFQ state.
 * @user_outcome Move RFQs through their lifecycle without switching to Odoo.
 *   Send reminders, add items, and award orders from one place.
 * @integration_point klyde_edge /rfq/send, /neo/graph/compare/rfq
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import {
  Send,
  BarChart3,
  Plus,
  Download,
  ExternalLink,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { useRFQComparison } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface RFQActionsProps {
  rfqId: string;
  projectId?: string | null;
}

export function RFQActions({ rfqId, projectId }: RFQActionsProps) {
  const rfqComparisonStrategy = useCapabilityStore((s) => s.strategyName('rfi', 'comparison'));
  const { data } = useRFQComparison(rfqComparisonStrategy!, { rfq_id: rfqId });

  const rfqStatus = data?.metadata?.rfq_status;
  const vendorCount = data?.vendors?.length ?? 0;
  const hasQuotes = data?.vendors?.some(v => v.response_status === 'quoted') ?? false;

  // Extract Odoo ID from semantic_id (format: "rfq:12345" or similar)
  // For RFQs in Odoo, we need the purchase.order or purchase.requisition ID
  const rfqSemanticId = data?.rfq_semantic_id;
  const rfqOdooId = rfqSemanticId?.split(':')[1];

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  const actions = [
    {
      label: 'Send to Vendors',
      description: rfqStatus === 'sent' ? 'Already sent to vendors' : 'Send RFQ to selected vendors',
      icon: Send,
      onClick: () => {
        toast.info('Send RFQ', {
          description: 'Vendor selection wizard coming soon',
        });
      },
      disabled: rfqStatus === 'sent' || rfqStatus === 'done',
      primary: rfqStatus === 'draft',
    },
    {
      label: 'Compare Quotes',
      description: hasQuotes ? `Compare ${vendorCount} vendor quotes` : 'No quotes received yet',
      icon: BarChart3,
      onClick: () => {
        toast.info('Compare Quotes', {
          description: 'Opening comparison view...',
        });
        // Could navigate to comparison canvas view
      },
      disabled: !hasQuotes,
      primary: hasQuotes,
    },
    {
      label: 'Add Products',
      description: 'Add more products to this RFQ',
      icon: Plus,
      onClick: () => {
        toast.info('Add Products', {
          description: 'Product selector coming soon',
        });
      },
      disabled: rfqStatus === 'done',
    },
    {
      label: 'Download PDF',
      description: 'Export RFQ as PDF document',
      icon: Download,
      onClick: () => {
        toast.info('Download', {
          description: 'PDF export coming soon',
        });
      },
    },
    {
      label: 'Open in Odoo',
      description: 'View full RFQ details',
      icon: ExternalLink,
      onClick: () => {
        if (rfqOdooId) {
          window.open(`${ODOO_HOST}/web#id=${rfqOdooId}&model=purchase.order&view_type=form`, '_blank');
        } else {
          toast.error('Cannot open', { description: 'RFQ ID not available' });
        }
      },
      disabled: !rfqOdooId,
    },
  ];

  return (
    <div className="space-y-4 p-3">
      {/* Header */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">RFQ ACTIONS</h4>
        <div className="p-2 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium truncate">{data?.rfq_name || rfqId}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs">
            <span className={`px-1.5 py-0.5 rounded ${
              rfqStatus === 'sent' ? 'bg-info/20 text-info' :
              rfqStatus === 'done' ? 'bg-success/20 text-success' :
              'bg-muted text-muted-foreground'
            }`}>
              {rfqStatus || 'loading...'}
            </span>
            <span className="text-muted-foreground">{vendorCount} vendors</span>
          </div>
        </div>
      </div>

      {/* Actions List */}
      <div className="space-y-2">
        {actions.map((action, i) => (
          <button
            key={i}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`
              flex items-center justify-between w-full p-3 rounded-lg
              transition-colors text-left
              ${action.primary
                ? 'bg-primary/10 hover:bg-primary/20 border border-primary/20'
                : 'hover:bg-accent border border-transparent'
              }
              ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-md ${action.primary ? 'bg-primary/20' : 'bg-muted'}`}>
                <action.icon className={`h-4 w-4 ${action.primary ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <div className="text-sm font-medium">{action.label}</div>
                <div className="text-xs text-muted-foreground">{action.description}</div>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}
